import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { AlertCircle, Inbox } from 'lucide-react';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { MediaGenerationJob } from 'modules/manager/domain/graphql/media-generation-jobs';
import { MEDIA_GENERATION_JOB_STATUS_LABELS, isActiveMediaGenerationJob } from 'modules/manager/domain/media-generation-job';
import { mediaKindLabel } from 'modules/manager/domain/media-kind';
import {
  useCancelMediaGenerationJobMutation,
  useDeleteMediaGenerationJobMutation,
  useDiscardMediaGenerationJobMutation,
  useMediaGenerationJobsQuery,
  useRetryMediaGenerationJobMutation,
} from 'modules/manager/store/media-generation-jobs-api';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import React, { useEffect, useMemo, useState } from 'react';
import { isDuplicateRequest } from 'shared/errors';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';
import UDEmptyState from 'ud-ui/components/empty-state';
import UDHeaderTitle from 'ud-ui/components/header-title';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

type QueueFilter = 'all' | 'drafts' | 'errors';

const POLL_MS = 15000;

const MediaQueuePage: React.FC = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { setHeaderContent } = useMainLayout();
  const { info } = useOrganizer();
  const tournamentId = info?.id ?? 0;
  const [filter, setFilter] = useState<QueueFilter>('all');

  const statuses = filter === 'drafts' ? ['ready'] : filter === 'errors' ? ['failed'] : undefined;
  const { data, currentData, isFetching, isError, refetch } = useMediaGenerationJobsQuery(
    { tournamentId, statuses, perPage: 50 },
    { skip: !tournamentId },
  );
  const [discardJob] = useDiscardMediaGenerationJobMutation();
  const [retryJob] = useRetryMediaGenerationJobMutation();
  const [cancelJob] = useCancelMediaGenerationJobMutation();
  const [deleteJob] = useDeleteMediaGenerationJobMutation();
  const [previewJobId, setPreviewJobId] = useState<number | null>(null);
  const [jobToDelete, setJobToDelete] = useState<MediaGenerationJob | null>(null);

  const items = data?.items ?? [];
  const previewJob = items.find((job) => job.id === previewJobId) ?? null;
  const hasActive = items.some(isActiveMediaGenerationJob);

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Очередь медиа</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  useEffect(() => {
    if (!hasActive) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [hasActive, refetch]);

  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: items.length,
        hasCurrentData: currentData !== undefined,
        isFetching,
        isError,
      }),
    [items.length, currentData, isFetching, isError],
  );

  const handleDiscard = async (job: MediaGenerationJob) => {
    try {
      await discardJob(job.id).unwrap();
    } catch (error) {
      console.error('Failed to discard media job:', error);
      toastApiError(error, msg`Не удалось отклонить`);
    }
  };

  const handleRetry = async (job: MediaGenerationJob) => {
    try {
      await retryJob(job.id).unwrap();
    } catch (error) {
      console.error('Failed to retry media job:', error);
      toastApiError(error, msg`Не удалось повторить`);
    }
  };

  const handleCancel = async (job: MediaGenerationJob) => {
    try {
      await cancelJob(job.id).unwrap();
    } catch (error) {
      console.error('Failed to cancel media job:', error);
      toastApiError(error, msg`Не удалось отменить`);
    }
  };

  const handleDelete = async () => {
    if (!jobToDelete) return;
    const closeDeleted = () => {
      if (previewJobId === jobToDelete.id) {
        setPreviewJobId(null);
      }
      setJobToDelete(null);
    };
    try {
      await deleteJob(jobToDelete.id).unwrap();
      closeDeleted();
    } catch (error) {
      // Код 15 — повтор: первый запрос задачу уже удалил, окно закрывается как при успехе.
      if (isDuplicateRequest(error)) {
        closeDeleted();
        return;
      }
      console.error('Failed to delete media job:', error);
      toastApiError(error, msg`Не удалось удалить`);
    }
  };

  return (
    // Те же отступы и ширина, что у списка шаблонов `/manager`: `container` в Tailwind 4 задаёт
    // только max-width, без полей и центровки, — контент прилипал к меню (IMGM-1493).
    <div className="flex h-full w-full flex-col p-6" data-testid="media-queue-page">
      <div className="mb-6 flex gap-2" data-testid="media-queue-filters">
        <UDButton
          variant={filter === 'all' ? 'primary' : 'secondary'}
          type="button"
          onClick={() => setFilter('all')}
          data-testid="media-queue-filter-all"
        >
          <Trans>Все</Trans>
        </UDButton>
        <UDButton
          variant={filter === 'drafts' ? 'primary' : 'secondary'}
          type="button"
          onClick={() => setFilter('drafts')}
          data-testid="media-queue-filter-drafts"
        >
          <Trans>Черновики</Trans>
        </UDButton>
        <UDButton
          variant={filter === 'errors' ? 'primary' : 'secondary'}
          type="button"
          onClick={() => setFilter('errors')}
          data-testid="media-queue-filter-errors"
        >
          <Trans>Ошибки</Trans>
        </UDButton>
      </div>

      <UDCard variant="elevated" padding="none">
        <UDAsyncContent
          state={state}
          // Отступы — как у `MediaTabStateCard`: у белой карточки нет своей высоты, без них пустота прилипнет к краю.
          empty={
            <UDEmptyState
              className="max-w-xl px-6 py-10"
              icon={<Inbox size={28} />}
              title={<Trans>В очереди пока нет задач</Trans>}
              subtitle={<Trans>Задачи появляются, когда на вкладке «Медиа» турнира генерируют картинки</Trans>}
            />
          }
          error={
            <UDEmptyState
              className="px-6 py-10"
              icon={<AlertCircle size={28} />}
              title={<Trans>Не удалось загрузить очередь</Trans>}
              subtitle={<Trans>Проверьте соединение и попробуйте ещё раз</Trans>}
              action={
                <UDButton variant="secondary" type="button" onClick={() => refetch()} data-testid="media-queue-retry">
                  <Trans>Повторить</Trans>
                </UDButton>
              }
            />
          }
        >
          <ul className="divide-lineSubtle divide-y">
            {items.map((job) => (
              <li key={job.id} className="flex items-center gap-4 px-4 py-3" data-testid={`media-queue-row-${job.id}`}>
                <div className="bg-surfaceSunken h-16 w-16 shrink-0 overflow-hidden rounded-md">
                  <button
                    type="button"
                    className="flex h-full w-full cursor-pointer items-center justify-center"
                    onClick={() => setPreviewJobId(job.id)}
                    data-testid={`preview-job-${job.id}`}
                    aria-label={t`Показать картинку`}
                  >
                    {job.image?.externalUrl ? (
                      <img src={job.image.externalUrl} alt="" className="h-full w-full object-cover" />
                    ) : job.status === 'PENDING' || job.status === 'RENDERING' ? (
                      <span className="text-inkMuted text-lg">⏳</span>
                    ) : (
                      <span className="text-inkMuted text-sm">!</span>
                    )}
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <UDText type="subhead" className="text-inkPrimary font-semibold">
                    {t(mediaKindLabel(job.kind))}
                  </UDText>
                  <UDText type="footnote" className="text-inkSecondary">
                    {job.championship.name}
                    {job.subjectTitle ? ` · ${job.subjectTitle}` : ''}
                  </UDText>
                  {job.text ? (
                    <UDText type="footnote" className="text-inkMuted truncate">
                      {job.text}
                    </UDText>
                  ) : null}
                  {job.error ? (
                    <UDText type="footnote" className="text-CaseErrorText">
                      {job.error}
                    </UDText>
                  ) : null}
                </div>
                <UDText type="footnote" className="text-inkMuted shrink-0">
                  {t(MEDIA_GENERATION_JOB_STATUS_LABELS[job.status])}
                </UDText>
                <div className="flex shrink-0 gap-2">
                  {job.status === 'READY' ? (
                    <UDButton
                      variant="secondary"
                      type="button"
                      onClick={() => handleDiscard(job)}
                      data-testid={`discard-job-${job.id}`}
                    >
                      <Trans>Отклонить</Trans>
                    </UDButton>
                  ) : null}
                  {job.status === 'FAILED' ? (
                    <UDButton variant="secondary" type="button" onClick={() => handleRetry(job)} data-testid={`retry-job-${job.id}`}>
                      <Trans>Повторить</Trans>
                    </UDButton>
                  ) : null}
                  {job.status === 'PENDING' ? (
                    <UDButton variant="secondary" type="button" onClick={() => handleCancel(job)} data-testid={`cancel-job-${job.id}`}>
                      <Trans>Отменить</Trans>
                    </UDButton>
                  ) : null}
                  {job.status !== 'RENDERING' ? (
                    <UDButton
                      variant="secondary"
                      type="button"
                      onClick={() => setJobToDelete(job)}
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trans>Удалить</Trans>
                    </UDButton>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </UDAsyncContent>
      </UDCard>

      {previewJob ? (
        <Modal show onClose={() => setPreviewJobId(null)} size="lg">
          <Modal.Header title={t(mediaKindLabel(previewJob.kind))} />
          <Modal.Body>
            {previewJob.image?.externalUrl ? (
              <img
                src={previewJob.image.externalUrl}
                alt=""
                className="mx-auto max-h-[70vh] w-auto max-w-full"
                data-testid="media-queue-preview-image"
              />
            ) : (
              <UDText type="subhead" className="text-inkSecondary py-10 text-center" data-testid="media-queue-preview-pending">
                {previewJob.status === 'PENDING' || previewJob.status === 'RENDERING' ? (
                  <Trans>Картинка ещё генерируется</Trans>
                ) : (
                  <Trans>Нет картинки</Trans>
                )}
              </UDText>
            )}
          </Modal.Body>
          <Modal.Footer>
            <UDButton variant="secondary" type="button" onClick={() => setPreviewJobId(null)}>
              <Trans>Закрыть</Trans>
            </UDButton>
          </Modal.Footer>
        </Modal>
      ) : null}

      <Modal.Confirm
        show={jobToDelete !== null}
        title={t`Удалить эту задачу?`}
        description={t`Картинка и запись будут удалены безвозвратно.`}
        onConfirm={handleDelete}
        onCancel={() => setJobToDelete(null)}
      />
    </div>
  );
};

export default MediaQueuePage;
